import { Modal, Skeleton, message as messageApi } from "antd";
import { usePaginate } from "hooks/usePagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DASHBOARD_REPAYMENT_ROUTE } from "routes";
import {
  getRepaymentHistory,
  getRepaymentStats,
  sendRepaymentEmailReminder,
} from "services/LoanService";
import InfoCard from "../Home/HomeCards/infoCards";
import { DateTime } from "luxon";

function StatusCard({ status = "PENDING" }) {
  if (status === "PENDING")
    return (
      <span className="inline-block px-2 ml-auto lg:px-4 text-xs border border-solid border-gray-200 font-medium rounded-lg py-2 text-gray-600 bg-gray-100">
        Pending
      </span>
    ); 
    if (status === "PARTIAL_REPAYMENT")
    return (
      <span className="inline-block px-2 ml-auto lg:px-4 text-xs border border-solid border-gray-200 font-medium rounded-lg py-2 text-gray-600 bg-gray-100">
        Partial Repayment
      </span>
    ); 
    if (status === "REPAID")
    return (
      <span className="inline-block px-2 ml-auto lg:px-4 text-xs border border-solid border-green-200 font-medium rounded-lg py-2 text-green-600 bg-green-100">
        Repaid
      </span>
    );
  if (status === "OVERDUE")
    return (
      <span className="inline-block px-2 ml-auto lg:px-4 text-xs border border-solid border-red-200 font-medium rounded-lg py-2 text-red-400 bg-red-100">
        Overdue
      </span>
    );
  return null;
}

function LoanCard({ loan }) {
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");
  const [loading, setLoading] = useState(false);

  const sendReminder = async () => {
    const trimmed = message.trim();

    if (trimmed.length === 0) {
      messageApi.error("Please add a message");
      return;
    }

    if (trimmed.length < 10) {
      messageApi.error("Message must be at least 10 characters long");
      return;
    }

    setLoading(true);

    try {
      const res = await sendRepaymentEmailReminder({
        id: loan.id,
        comment: trimmed,
      });

      if (res.err) {
        throw new Error(res.message);
      }

      setModalOpen(false);
    } catch (error) {
      messageApi.error(
        error?.message ?? "An error occurred, could not send reminder"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(open ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [open]);

  const dueDate = useMemo(() => {
    const jsDate = new Date(loan.institution.graduation_year);

    return DateTime.fromJSDate(jsDate)
      .plus({ year: 1 })
      .toFormat("dd LLL, yyyy");
  }, [loan]);

  const formatDate = (date) => {
    if (!date) return "";
    const jsDate = new Date(date);
    return DateTime.fromJSDate(jsDate).toFormat("dd LLL, yyyy");
  };

  const status = useMemo(() => {
    if(loan.repayment_status) return loan.repayment_status;
    const jsDate = new Date(loan.institution.graduation_year);
    jsDate.setDate(30);

    const timeDiff = DateTime.fromJSDate(jsDate).plus({ year: 1 }).diffNow();

    if (timeDiff.milliseconds > 0) {
      return "PENDING";
    }
    return "OVERDUE";
  }, [loan]);
  return (
    <>
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        centered
        footer={null}
      >
        <div className="w-full">
          <p className="text-lg lg:text-2xl font-poppins font-medium">
            Send A Message
          </p>

          <div className="my-6 space-y-6">
            {/* Amount */}
            <div className="w-full">
              <p className="text-xs lg:text-sm font-poppins text-primary-700 mb-2">
                Message
              </p>
              <textarea
                className="text-primary-900 p-3 border w-full rounded-lg border-solid border-primary-200 
                     font-poppins disabled:opacity-60 disabled:cursor-not-allowed"
                type="number"
                required
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                }}
              />
            </div>

            {/* Buttons */}
            <div
              onClick={sendReminder}
              className="flex flex-col sm:flex- disabled:opacity-50 items-stretch sm:items-center gap-y-3 sm:gap-x-3 sm:justify-end pt-3"
            >
              <button
                disabled={loading}
                className="bg-primary border border-solid text-white rounded-lg px-6 py-3 text-base font-poppins w-full sm:w-auto"
              >
                {loading ? "Sending" : "Send"}
              </button>
              <button
                disabled={loading}
                className="bg-red-400 text-white disabled:opacity-50 rounded-lg px-6 py-3 text-base font-poppins w-full sm:w-auto"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <li className="bg-white rounded-lg shadow">
        <header className="p-4 relative border-b border-solid border-primary-100">
          <div className="lg:flex space-y-2 lg:space-y-0 items-start ">
            <div className="mb-2">
              <div className="text-right absolute right-4 lg:hidden">
                <StatusCard status={status} />
              </div>
              <p className="mb-3 text-primary font-medium">
                {loan?.applicant?.first_name}
                {" " + loan?.applicant?.surname + " "}
                {loan?.applicant?.middle_name}
              </p>
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Amount Due
              </p>
              <p className="text-lg lg:text-2xl font-poppins text-primary font-medium">
                ₦{loan.due_amount.toLocaleString()}
              </p>
              <div className="flex items-center gap-x-1">
                <p className="text-xs lg:text-sm font-poppins text-gray-600">
                  Amount Paid:{" "}
                </p>
                <p className="text-xs lg:text-sm font-poppins text-primary font-medium">
                  ₦{loan?.total_amount_paid?.toLocaleString()}
                </p>
              </div>
            </div>

            {loan?.repayment_status !== "REPAID" 
            ?<div className="lg:ml-auto mb-4 lg:mb-0 lg:text-right">
              <button
                onClick={() => setModalOpen(true)}
                className="hidden lg:inline-block bg-primary border border-solid text-white rounded-lg px-4 py-2 text-xs lg:text-sm"
              >
                Send a message
              </button>

              <div className="mt-1 mb-4 lg:mb-0 flex items-center gap-x-1">
                <p className="text-xs lg:text-sm font-poppins text-gray-600">
                  Due on:
                </p>
                <p className="text-xs lg:text-sm font-poppins text-primary font-medium">
                  {dueDate}
                </p>
              </div>
            </div>
            :<div className="lg:ml-auto mb-4 lg:mb-0 lg:text-right">
              <div className="mt-1 mb-4 lg:mb-0 items-center gap-x-1">
                <p className="text-xs lg:text-sm font-poppins text-gray-600">
                  Repaid at:
                </p>
                <p className="text-xs lg:text-sm font-poppins text-primary font-medium">
                  {formatDate(loan?.latest_repayment?.timestamp) || "N/A"}
                </p>
              </div>
            </div>}
          </div>

          <div className="flex items-center gap-x-4">
            <div className="hidden lg:inline-block">
              <StatusCard status={status} />
            </div>
            {loan.repayment_status !== "REPAID" && (
              <button
              onClick={() => setModalOpen(true)}
              className="lg:hidden bg-primary border border-solid text-white rounded-lg px-4 py-2 text-xs lg:text-sm"
            >
              Send a message
            </button>
            )}

            <button
              className="bg-primary-100 ml-auto font-medium border border-solid border-primary-300 text-primary-600 rounded-lg px-4 py-2 text-xs"
              onClick={() => setOpen(!open)}
            >
              {open ? "Hide Details" : "See Details"}
            </button>
          </div>
        </header>

        {/* Collapsible with smooth slide */}
        <div
          ref={contentRef}
          style={{ maxHeight }}
          className="overflow-hidden transition-all duration-300 ease-in-out"
        >
          <div className="p-4 lg:px-8 grid gap-4 lg:grid-cols-4">
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Student Name:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {loan?.applicant?.first_name}
                {" " + loan?.applicant?.surname + " "}
                {loan?.applicant?.middle_name}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Student Email:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {loan?.applicant?.email}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Student Phone Number:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {loan?.applicant?.phone}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Institution:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {loan?.institution?.name}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Graduation Year:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {formatDate(loan?.institution?.graduation_year)}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                LGA:
              </p>
              <p className="font-medium capitalize text-xs lg:text-sm text-gray-800 font-poppins">
                {loan?.applicant?.lga}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Repayment Rate
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                85%
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Application Date:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {loan?.createdAt}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Date Disbursed:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {formatDate(loan?.disburse_date) || "N/A"}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Last Payment Date:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {formatDate(loan?.latest_repayment?.timestamp) || "N/A"}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Total Loan Amount
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                ₦{loan?.amount?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </li>
    </>
  );
}

function Loader() {
  return (
    <section className="space-y-4 mt-4">
      {Array(4)
        .fill("")
        .map(() => (
          <div className="shadow bg-white flex justify-between p-4 rounded-lg">
            <div className="flex-1 space-y-2">
              <div>
                <Skeleton.Button style={{ height: "14px" }} active />
              </div>
              <div className="max-w-[200px]">
                <Skeleton.Button block style={{ height: "34px" }} active />
              </div>
              <div className="max-w-[200px]">
                <Skeleton.Button block style={{ height: "14px" }} active />
              </div>
            </div>
            <div className="flex-1 text-right space-y-2">
              <div className="max-w-[200px] ml-auto">
                <Skeleton.Button block style={{ height: "24px" }} active />
              </div>
              <div>
                <Skeleton.Button style={{ height: "14px" }} active />
              </div>
              <div className="max-w-[150px] ml-auto">
                <Skeleton.Button block style={{ height: "16px" }} active />
              </div>
            </div>
          </div>
        ))}
    </section>
  );
}

function EmptyRepayments() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
      <svg
        className="w-16 h-16 mb-4 text-gray-300"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 4h6m-6 0a2 2 0 01-2-2m8 2a2 2 0 002-2"
        />
      </svg>
      <p className="text-sm sm:text-base font-poppins">
        No Data Found.
      </p>
    </div>
  );
}


function RepaymentList({
  loading = false,
  loans = [],
  isError = false,
  errMessage = "",
}) {
  if (loading) {
    return <Loader />;
  }
  if(loans.length === 0){
    return (
      <div className="shadow bg-white flex justify-center p-4 rounded-lg my-8">
        <EmptyRepayments/>
      </div>
    )
  }
  return (
    <ul className="space-y-4 my-4">
      {loans.map((item) => (
        <LoanCard loan={item} />
      ))}
    </ul>
  );
}

function CustomSelect({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || "All";

  return (
    <div className="relative z-[100] w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="
          w-full pl-10 pr-3 py-2.5
          bg-gray-50
          border border-gray-200
          outline-0
          rounded-lg
          text-sm font-medium
          text-left
          transition-all
          flex justify-between items-center
        "
      >
        {selectedLabel}
        <span className="ml-2">&#9662;</span> {/* simple down arrow */}
      </button>

      {isOpen && (
        <ul className="absolute z-1000 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-md max-h-60 overflow-auto">
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className="
                px-3 py-2 text-sm cursor-pointer
                hover:bg-gray-100
                transition-colors
              "
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function RepaymentLoans() {
  const [loans, setLoans] = useState([]);
  const [stats, setStats] = useState({
    total_disbursed_loans: 0,
    total_repayment: 0,
    total_repaid_loans: 0,
    total_unpaid_loans: 0,
  });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [total, setTotal] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errMessage, setErrMessage] = useState();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400); // delay typing effect

    return () => clearTimeout(timeout);
  }, [search]);

  const { page, setPage, setLimit, limit } = usePaginate({
    baseRoute: DASHBOARD_REPAYMENT_ROUTE,
  });


  const getStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await getRepaymentStats();
      if (res.data) {
        setStats(res.data);
      }
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchRepaymentHistory = useCallback(async () => {
    setLoading(true);
    const id = ++requestIdRef.current; // unique id for each request

    try {
      const res = await getRepaymentHistory({
        page,
        limit,
        search: debouncedSearch,
        status,
      });

      // If a new request has been fired since this one started → ignore this result
      if (id !== requestIdRef.current) return;

      if (!res.data || res.error) {
        throw new Error(res.message ?? "Could not get repayment history");
      }

      setLoans(res.data.loans ?? []);
      setTotal(res.data.total ?? 1);
      setTotalPages(res.data.pagination?.totalPages ?? 1);
    } catch (err) {
      if (id !== requestIdRef.current) return; // ignore stale error too
      setErrMessage(err?.message ?? "Could not get repayment history");
      setIsError(true);
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
  }, [limit, page, debouncedSearch, status]);

  function generatePages(current, total) {
    const pages = [];

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    pages.push(1);

    if (current > 3) pages.push("...");
    if (current > 2) pages.push(current - 1);

    if (current !== 1 && current !== total) pages.push(current);

    if (current < total - 1) pages.push(current + 1);
    if (current < total - 2) pages.push("...");

    pages.push(total);

    return pages;
  }

  useEffect(() => {
    getStats();
  }, [getStats]);

  useEffect(() => {
    fetchRepaymentHistory();
  }, [fetchRepaymentHistory]);

  return (
    <>
      <section>
        <header className="text-center sm:text-left mb-4 sm:mb-0">
          <p className="font-poppins text-base sm:text-xl font-semibold text-primary">
            Repayment History
          </p>
          <p className="font-poppins text-xs sm:text-sm text-gray-500">
            Loans with upcoming or current repayment due dates.
          </p>
        </header>
        <div className="grid grid-cols-4 gap-4 my-4">
          <InfoCard
            loading={loadingStats}
            loanType={"all"}
            title="Total Disbursed"
            value={stats.total_disbursed_loans.toLocaleString()}
          />
          <InfoCard
            loanType={"approved"}
            loading={loadingStats}
            title="Total Repayment Amount"
            value={`₦${stats.total_repayment.toLocaleString()}`}
          />
          <InfoCard
            loanType={"pending"}
            loading={loadingStats}
            title="Fully Repaid Loans"
            value={stats.total_repaid_loans.toLocaleString()}
          />
          <InfoCard
            loanType={"rejected"}
            loading={loadingStats}
            title="Total Outstanding Loans"
            value={stats.total_unpaid_loans.toLocaleString()}
          />
          {/* <InfoCard
            loanType={"disbursed"}
            title="Total Loan Disbursed"
            value={"0"}
          />
          <InfoCard
            loanType={"repaid"}
            title="Total Loans Repaid"
            value={"0"}
          /> */}
        </div>
        <header className="mt-5">
          <div
            className="
      bg-white backdrop-blur-md 
      border border-gray-100 
      relative
      z-[100]
      shadow-sm 
      rounded-xl 
      p-5 
      grid grid-cols-1
      gap-4
    "
          >
            <p className="font-medium">Filters</p>
            <div className="flex gap-4">
              <div className="flex-[2]">
                {/* Search */}
                <div className="relative w-full">
                  <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    className="
          w-full pl-10 pr-3 py-2.5
          bg-gray-50 
          border border-gray-200
          outline-0
          rounded-lg
          text-sm font-medium
          transition-all
        "
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, LGA or institution"
                  />
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={() => setSearch("")}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
              <div className="flex-1">
                <CustomSelect
                  value={status}
                  onChange={(value)=>{
                    setStatus(value)
                    setPage(1)
                  }}
                  options={[
                    { value: "", label: "All",},
                    { value: "OVERDUE", label: "Overdue",},
                    { value: "PARTIAL_REPAYMENT", label: "Partial Repayment",},
                    { value: "REPAID", label: "Full Repayment",},
                  ]}
                />
              </div>
            </div>
          </div>
        </header>
        <RepaymentList
          loading={loading}
          loans={loans}
          isError={isError}
          errMessage={errMessage}
        />
        <div className="flex items-center justify-between mt-6">
          {/* Showing info */}
          <p className="text-sm text-gray-500 font-medium">
            Showing{" "}
            <span className="text-gray-700">
              {(page - 1) * limit + 1}–{Math.min(page * limit, total)}
            </span>{" "}
            / {total} results
          </p>

          {/* Pagination */}
          <div className="flex items-center gap-3">
            {/* Limit Selector */}
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="
        px-3 py-2 rounded-lg border border-gray-200 bg-white/80 backdrop-blur
        text-sm font-medium text-gray-700 hover:bg-gray-100 transition
      "
            >
              {[10, 20, 50, 100].map((v) => (
                <option key={v} value={v}>
                  {v} per page
                </option>
              ))}
            </select>

            {/* Prev */}
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="
        px-3 py-2 rounded-lg border border-gray-200 bg-white/70 backdrop-blur
        text-gray-600 hover:bg-gray-100 transition flex items-center gap-1
        disabled:opacity-40 disabled:cursor-not-allowed
      "
            >
              <i className="bi bi-chevron-left"></i>
              <span className="hidden sm:inline">Prev</span>
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-2">
              {generatePages(page, totalPages).map((p, i) =>
                p === "..." ? (
                  <span key={i} className="px-3 py-2 text-gray-400 text-sm">
                    …
                  </span>
                ) : (
                  <button
                    key={i}
                    onClick={() => setPage(p)}
                    className={`
              px-3 py-2 rounded-lg text-sm font-medium transition
              ${
                p === page
                  ? "bg-primary text-white shadow-sm"
                  : "bg-white/60 border border-gray-200 text-gray-700 hover:bg-gray-100"
              }
            `}
                  >
                    {p}
                  </button>
                )
              )}
            </div>

            {/* Next */}
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              className="
        px-3 py-2 rounded-lg border border-gray-200 bg-white/70 backdrop-blur
        text-gray-600 hover:bg-gray-100 transition flex items-center gap-1
        disabled:opacity-40 disabled:cursor-not-allowed
      "
            >
              <span className="hidden sm:inline">Next</span>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
